import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api/client'
import {
  busInventarHolen,
  streckeHolen,
  type BusInventar,
  type BusInventarPgn,
  type Tagesstrecke,
} from '../api/strecke'
import { Karte } from '../komponenten/KarteLazy'
import type { Kartenpunkt } from '../komponenten/Karte'
import { FELD, telemetriefelder, wertText } from '../komponenten/telemetrie'
import { KATEGORIE_LABEL, pgnHex, pgnInfo, type PgnKategorie } from '../komponenten/pgn'

/**
 * Die gefahrene Strecke eines Betriebstags als eigene Seite — ein Vorgang, eine Adresse.
 *
 * <h2>Was hier zu sehen ist</h2>
 *
 * <p>Die rohe Spur: die gemeldeten Punkte in zeitlicher Reihenfolge, mit einer Linie verbunden.
 * Nichts wird auf Straßen gezogen und nichts geglättet — ein Sprung im GPS-Fix bleibt sichtbar,
 * denn wer hier nachsieht, will wissen, was das Gerät gesendet hat, nicht, was eine schöne
 * Route gewesen wäre. Daneben steht, was der Bus sonst über sich meldet.
 *
 * <h2>Betriebstag, nicht Kalendertag</h2>
 *
 * <p>Der Datumswähler meint den Betriebstag (04:00–04:00). Wer „heute" ansieht und es ist
 * 01:30 nachts, bekommt die laufende Nacht — nicht einen leeren neuen Kalendertag.
 *
 * <h2>Woher die Daten kommen</h2>
 *
 * <p>Aus dem Rohspeicher auf dem Backend-Server, über die Backend-API. Das Fahrzeug wird über
 * seine Telematik-Kennung zugeordnet. Fehlt sie, oder ist keine Backend-API hinterlegt, sagt
 * der Server das in Klartext — und diese Seite zeigt genau das.
 */

/** Der laufende Betriebstag als `YYYY-MM-DD` — vor 04:00 zählt die Nacht noch zum Vortag. */
function betriebstagHeute(): string {
  const d = new Date()
  if (d.getHours() < 4) d.setDate(d.getDate() - 1)
  const z = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`
}

/** Nur die Uhrzeit — für Start und Ende und in den Kennzahlen. */
function uhrzeit(wert: string | null | undefined): string {
  if (!wert) return '—'
  return new Date(wert).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

/** Sekundengenaue Uhrzeit — für den einzelnen, angeklickten Punkt. */
function uhrzeitGenau(wert: string | null): string {
  if (!wert) return '—'
  return new Date(wert).toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

/** Ein Betriebstag menschlich: `19.10.2026`. */
function tagLang(iso: string): string {
  const [j, m, t] = iso.split('-')
  return `${t}.${m}.${j}`
}

/**
 * Die Positionsfelder stehen schon auf der Karte; die Telemetrie zeigt, was der Bus sonst über
 * sich meldet. Beschriftung, Reihenfolge und das Anhängen unbekannter Felder kommen aus dem
 * gemeinsamen Modul {@link telemetriefelder}, damit Streckenseite und Fahrzeugakte dieselben
 * Namen zeigen.
 */
const NICHT_IN_TELEMETRIE = new Set(['breite', 'laenge', 'richtung'])

/** Farbe je PGN-Kategorie für das kleine Abzeichen in der Bus-Inventar-Karte. */
const KATEGORIE_FARBE: Record<PgnKategorie, string> = {
  kern: '#16a34a',
  zustand: '#2563eb',
  transport: '#6b7280',
  proprietaer: '#b45309',
}

/** Senderate menschlich: unter 1 Hz zwei Nachkommastellen, sonst eine. */
function rate(hz: number): string {
  if (!Number.isFinite(hz)) return '—'
  return `${hz < 1 ? hz.toFixed(2) : hz.toFixed(1)} Hz`
}

interface Kopf {
  kennzeichen: string
  interneNummer: string | null
  telematikId: string | null
}

export function FahrzeugStrecke() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [kopf, setKopf] = useState<Kopf | null>(null)
  const [strecke, setStrecke] = useState<Tagesstrecke | null>(null)
  const [inventar, setInventar] = useState<BusInventar | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [tag, setTag] = useState<string>(betriebstagHeute())
  const [automatisch, setAutomatisch] = useState(true)
  /** Index des angeklickten Punktes in der Spur, oder null. */
  const [ausgewaehlt, setAusgewaehlt] = useState<number | null>(null)
  /**
   * Ob die Karte den Ausschnitt an die Punkte anpasst. Nur beim sichtbaren Laden und beim
   * Tageswechsel — beim stillen 15-s-Nachladen nicht, sonst springt die Karte jedem zurück,
   * der gerade hineingezoomt hat.
   */
  const [neuEinpassen, setNeuEinpassen] = useState(true)

  const heute = betriebstagHeute()
  const istHeute = tag === heute

  const laden = useCallback(
    async (sichtbar = true) => {
      if (!id) return
      if (sichtbar) setLaeuft(true)
      try {
        const frisch = await streckeHolen(Number(id), tag)
        setStrecke(frisch)
        setNeuEinpassen(sichtbar)
        setFehler(null)
      } catch (f) {
        setFehler(f instanceof Error ? f.message : String(f))
        setStrecke(null)
      } finally {
        if (sichtbar) setLaeuft(false)
      }
    },
    [id, tag],
  )

  // Kopfdaten (Kennzeichen, Telematik-Kennung) einmal laden — für Titel und Hinweise.
  useEffect(() => {
    if (!id) return
    api
      .get<Kopf>(`/api/fahrzeuge/${id}`)
      .then(setKopf)
      .catch(() => setKopf(null))
  }, [id])

  // Das Bus-Inventar ist „zuletzt gesehen", nicht an den Betriebstag gebunden — einmal je
  // Fahrzeug laden. Fehlt es (204) oder streikt die Backend-API, bleibt die Karte einfach weg.
  useEffect(() => {
    if (!id) return
    busInventarHolen(Number(id))
      .then(setInventar)
      .catch(() => setInventar(null))
  }, [id])

  // Beim Tageswechsel die Auswahl verwerfen — die Punkte sind dann andere.
  useEffect(() => {
    setAusgewaehlt(null)
    void laden()
  }, [laden])

  // Nur für heute automatisch nachladen: Ein vergangener Tag ändert sich nicht mehr, und ein
  // Bus, der gerade fährt, soll auf der Karte weiterwachsen, ohne dass jemand klickt.
  useEffect(() => {
    if (!automatisch || !istHeute) return
    const uhr = setInterval(() => void laden(false), 15_000)
    return () => clearInterval(uhr)
  }, [automatisch, istHeute, laden])

  // Die Spur für die Karte: Punkte in Reihenfolge, Beschriftung nur an Start und Ende. Stabil
  // halten, damit der 15-Sekunden-Takt die Karte nicht bei jedem Durchlauf neu aufbaut.
  const spur = useMemo<Kartenpunkt[]>(() => {
    const p = strecke?.punkte ?? []
    return p.map((punkt, i) => {
      const letzter = i === p.length - 1
      return {
        breite: punkt.breite,
        laenge: punkt.laenge,
        titel:
          i === 0
            ? `Start ${uhrzeit(punkt.gemessenAm)}`
            : letzter
              ? `${istHeute ? 'Zuletzt' : 'Ende'} ${uhrzeit(punkt.gemessenAm)}`
              : undefined,
        richtung: letzter ? punkt.richtung : null,
      }
    })
  }, [strecke, istHeute])

  // Stabil, damit das Setzen der Kreis-Marker nicht bei jedem Render neu läuft.
  const waehlen = useCallback((i: number) => setAusgewaehlt(i), [])

  const hatPunkte = (strecke?.anzahlPunkte ?? 0) > 0
  const punkt =
    strecke && ausgewaehlt !== null ? strecke.punkte[ausgewaehlt] ?? null : null
  const zustand = strecke?.letzterZustand ?? null

  const telemetrieFelder = useMemo(
    () => (zustand ? telemetriefelder(zustand, NICHT_IN_TELEMETRIE) : []),
    [zustand],
  )

  // Die PGNs des Inventars: zugeordnete zuerst (die liest man zuerst), Unbekanntes dahinter;
  // je Gruppe nach PGN-Nummer. So steht der fahrbare Kern oben, der Rest ehrlich darunter.
  const pgnReihe = useMemo<BusInventarPgn[]>(() => {
    const liste = inventar?.pgns ?? []
    return [...liste].sort((a, b) => {
      const za = pgnInfo(a.pgn) ? 0 : 1
      const zb = pgnInfo(b.pgn) ? 0 : 1
      return za !== zb ? za - zb : a.pgn - b.pgn
    })
  }, [inventar])

  const titel =
    kopf?.kennzeichen ?? (kopf?.interneNummer ? `Wagen ${kopf.interneNummer}` : '')

  return (
    <>
      <div className="inhalt-kopf">
        <div>
          <button
            type="button"
            className="knopf-schlicht"
            onClick={() => navigate(`/fahrzeuge/${id}`)}
            style={{ marginBottom: 8 }}
          >
            ← Fahrzeugakte
          </button>
          <h1>Heute gefahren{titel && ` · ${titel}`}</h1>
          <p>Die zurückgelegte Route des Betriebstags — roh aus dem Rohspeicher, ohne Glättung.</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <label style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
            Betriebstag{' '}
            <input
              type="date"
              value={tag}
              max={heute}
              onChange={(e) => setTag(e.target.value || heute)}
              style={{ marginLeft: 6 }}
            />
          </label>
          {!istHeute && (
            <button type="button" className="knopf-schlicht" onClick={() => setTag(heute)}>
              Heute
            </button>
          )}
          {istHeute && (
            <label style={{ color: 'var(--grau-500)', fontSize: '0.85rem' }}>
              <input
                type="checkbox"
                checked={automatisch}
                onChange={(e) => setAutomatisch(e.target.checked)}
                style={{ marginRight: 6 }}
              />
              alle 15 s
            </label>
          )}
          <button
            type="button"
            className="knopf-primaer"
            onClick={() => void laden()}
            disabled={laeuft}
          >
            {laeuft ? 'Wird geladen …' : 'Aktualisieren'}
          </button>
        </div>
      </div>

      {fehler && <div className="meldung fehler">{fehler}</div>}

      {strecke && (
        <div className="karte" style={{ padding: 18, marginBottom: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 10,
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <h2 style={{ fontSize: '0.95rem' }}>
              {istHeute ? 'Heute' : tagLang(strecke.betriebstag)}
            </h2>
            <small style={{ color: 'var(--grau-500)' }}>
              {hatPunkte
                ? `${strecke.anzahlPunkte} Punkte · ${uhrzeit(strecke.vonAm)}–${uhrzeit(strecke.bisAm)} · ${strecke.streckeKm.toFixed(1)} km (roh)`
                : 'keine Position an diesem Tag'}
            </small>
          </div>

          {hatPunkte ? (
            <>
              <Karte
                spur={spur}
                punkte={[]}
                hoehe={480}
                beimPunktKlick={waehlen}
                einpassen={neuEinpassen}
              />
              {punkt ? (
                <p style={{ marginTop: 8, fontSize: '0.85rem', color: 'var(--grau-600, #556)' }}>
                  <strong>Punkt {uhrzeitGenau(punkt.gemessenAm)}</strong>
                  {' · '}
                  {punkt.geschwindigkeit ?? '—'} km/h
                  {' · '}
                  Richtung {punkt.richtung ?? '—'}°{' · '}
                  {punkt.breite.toFixed(6)}, {punkt.laenge.toFixed(6)}
                </p>
              ) : (
                <p style={{ marginTop: 8, fontSize: '0.85rem', color: 'var(--grau-500)' }}>
                  Einen Punkt der Route anklicken zeigt seine Zeit, Geschwindigkeit und Richtung.
                </p>
              )}
            </>
          ) : (
            <p className="unbesetzt" style={{ fontSize: '0.9rem' }}>
              Von diesem Fahrzeug liegt für {istHeute ? 'heute' : 'diesen Betriebstag'} keine
              Position im Rohspeicher. Entweder hat das Gerät nicht gesendet, oder es sendet ohne
              GPS — ein reiner FMS-Aufsatz trägt keine Koordinaten.
            </p>
          )}
        </div>
      )}

      {strecke &&
        (strecke.hoechsteGeschwindigkeit !== null ||
          strecke.kmGefahren !== null ||
          zustand) && (
          <div className="karte" style={{ padding: 18 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                marginBottom: 12,
              }}
            >
              <h2 style={{ fontSize: '0.95rem' }}>Telemetrie des Tages</h2>
              {zustand?.gemessenAm != null && (
                <small style={{ color: 'var(--grau-500)' }}>
                  zuletzt gemessen {uhrzeit(String(zustand.gemessenAm))}
                </small>
              )}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                gap: 14,
              }}
            >
              <div>
                <div style={{ color: 'var(--grau-500)', fontSize: '0.78rem' }}>
                  Höchste Geschwindigkeit
                </div>
                <div style={{ fontSize: '1.15rem', fontWeight: 600 }}>
                  {strecke.hoechsteGeschwindigkeit !== null
                    ? `${strecke.hoechsteGeschwindigkeit} km/h`
                    : '—'}
                </div>
              </div>
              <div>
                <div style={{ color: 'var(--grau-500)', fontSize: '0.78rem' }}>
                  Gefahren (Zähler)
                </div>
                <div style={{ fontSize: '1.15rem', fontWeight: 600 }}>
                  {strecke.kmGefahren !== null ? `${strecke.kmGefahren} km` : '—'}
                </div>
              </div>
              <div>
                <div style={{ color: 'var(--grau-500)', fontSize: '0.78rem' }}>
                  Strecke (roh, GPS)
                </div>
                <div style={{ fontSize: '1.15rem', fontWeight: 600 }}>
                  {strecke.streckeKm.toFixed(1)} km
                </div>
              </div>
            </div>

            {zustand && telemetrieFelder.length > 0 && (
              <>
                <p style={{ marginTop: 14, marginBottom: 6, fontSize: '0.8rem', color: 'var(--grau-500)' }}>
                  Letzter vollständiger Zustand — alles, was das Gerät über sich meldet:
                </p>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '170px 1fr',
                    gap: '2px 12px',
                    fontSize: '0.88rem',
                  }}
                >
                  {telemetrieFelder.map((feld) => {
                    const def = FELD[feld]
                    const roh = zustand[feld]
                    const text =
                      feld === 'gemessenAm' ? uhrzeit(String(roh)) : wertText(roh)
                    return (
                      <Fragment key={feld}>
                        <span style={{ color: 'var(--grau-500)', padding: '3px 0' }}>
                          {def?.label ?? feld}
                        </span>
                        <span style={{ padding: '3px 0' }}>
                          {text}
                          {def?.einheit && text !== '—' ? ` ${def.einheit}` : ''}
                        </span>
                      </Fragment>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        )}

      {inventar && pgnReihe.length > 0 && (
        <div className="karte" style={{ padding: 18, marginTop: 20 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 4,
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <h2 style={{ fontSize: '0.95rem' }}>Bus-Inventar</h2>
            <small style={{ color: 'var(--grau-500)' }}>
              {inventar.gemessenAm
                ? `gesehen ${uhrzeit(inventar.gemessenAm)}`
                : inventar.empfangenAm
                  ? `empfangen ${uhrzeit(inventar.empfangenAm)}`
                  : ''}
            </small>
          </div>
          <p style={{ marginTop: 0, marginBottom: 12, fontSize: '0.8rem', color: 'var(--grau-500)' }}>
            Welche PGNs der Fahrzeugbus sendet — roh aus dem Gerät, mit unserer Zuordnung
            beschriftet. {inventar.pgnAnzahl ?? pgnReihe.length} PGNs
            {inventar.pgnBekannt != null && `, davon ${inventar.pgnBekannt} vom Gerät dekodiert`}
            {inventar.fensterSekunden != null && ` · Fenster ${Math.round(inventar.fensterSekunden)} s`}
            {inventar.rahmenGesamt != null && ` · ${inventar.rahmenGesamt.toLocaleString('de-DE')} Rahmen`}
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
              <thead>
                <tr
                  style={{
                    textAlign: 'left',
                    color: 'var(--grau-500)',
                    borderBottom: '1px solid var(--grau-200, #e5e7eb)',
                  }}
                >
                  <th style={{ padding: '4px 8px 4px 0' }}>PGN</th>
                  <th style={{ padding: '4px 8px' }}>Bedeutung</th>
                  <th style={{ padding: '4px 8px' }}>SA</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Rate</th>
                  <th style={{ padding: '4px 8px', textAlign: 'right' }}>Rahmen</th>
                  <th style={{ padding: '4px 0 4px 8px' }}>Bytes</th>
                </tr>
              </thead>
              <tbody>
                {pgnReihe.map((p) => {
                  const info = pgnInfo(p.pgn)
                  return (
                    <tr
                      key={`${p.pgn}-${p.id}`}
                      style={{ borderBottom: '1px solid var(--grau-100, #f1f3f5)' }}
                    >
                      <td style={{ padding: '4px 8px 4px 0', whiteSpace: 'nowrap' }}>
                        <strong>{p.pgn}</strong>{' '}
                        <span
                          style={{
                            color: 'var(--grau-500)',
                            fontFamily: 'monospace',
                            fontSize: '0.78rem',
                          }}
                        >
                          {pgnHex(p.pgn)}
                        </span>
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        {info ? (
                          <>
                            <span>{info.label}</span>{' '}
                            <span
                              style={{
                                fontSize: '0.68rem',
                                color: '#fff',
                                background: KATEGORIE_FARBE[info.kategorie],
                                borderRadius: 4,
                                padding: '1px 6px',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {KATEGORIE_LABEL[info.kategorie]}
                            </span>
                            {info.hinweis && (
                              <div style={{ fontSize: '0.72rem', color: 'var(--grau-500)' }}>
                                {info.hinweis}
                              </div>
                            )}
                          </>
                        ) : (
                          <span style={{ color: 'var(--grau-500)' }}>
                            — nicht zugeordnet{p.tp ? ' (Transportprotokoll)' : ''}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>
                        {p.sa?.join(', ') ?? '—'}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {rate(p.hz)}
                      </td>
                      <td style={{ padding: '4px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {p.n.toLocaleString('de-DE')}
                      </td>
                      <td
                        style={{
                          padding: '4px 0 4px 8px',
                          fontFamily: 'monospace',
                          fontSize: '0.76rem',
                          color: 'var(--grau-600, #556)',
                        }}
                      >
                        {p.byte}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!strecke && !fehler && <div className="laden">Wird geladen …</div>}
    </>
  )
}

export default FahrzeugStrecke
